import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Food.css";

interface FoodItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Food: React.FC = () => {
  const [data, setData] = useState<FoodItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/food`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching food:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: FoodItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: FoodItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Food",
    de: "Essen",
    ta: "உணவு",
    si: "ආහාර",
    hi: "भोजन",
    ar: "طعام",
    tr: "Yemek",
    ru: "Еда",
    zh: "食物",
    fr: "Nourriture",
    es: "Comida",
  };

  const subtitleMap = {
    en: "Food Items in German",
    de: "Lebensmittel auf Deutsch",
    ta: "ஜெர்மன் மொழியில் உணவு பொருட்கள்",
    si: "ජර්මන් භාෂාවෙන් ආහාර භාණ්ඩ",
    hi: "जर्मन में खाद्य पदार्थ",
    ar: "الأطعمة بالألمانية",
    tr: "Almanca'da Yiyecekler",
    ru: "Продукты на немецком",
    zh: "德语食物",
    fr: "Nourriture en allemand",
    es: "Comida en alemán",
  };

  return (
    <div className="food-page-container">
      <div className="food-page-header">
        <div>
          <h1 className="food-page-title">{titleMap["de"]}</h1>
          <p className="food-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="food-page-loading">Loading...</div>
      ) : (
        <div className="food-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`food-page-card ${
                  isPlaying ? "food-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="food-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="food-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="food-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="food-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="food-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Food;
